import React, { useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import type { JsonQuestion, JsonQuestionOption, UnitInputHint } from './types';
import { getQuestionKey } from './questionKey';
import { isEffectivelyRequired } from './effectiveRequired';
import type { QuestionnaireDemographicContext } from './clientContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Tag, TagList } from '@/components/ui/tag';
import { cn } from '@/lib/utils';
import { formatDisplayLabel } from '@/utils/display-labels';
import { isNoneOption } from './noneOptions';

export interface NumberUnitAnswerValue {
  value: string | number;
  unit: string;
}

export type JsonAnswerValue = string | string[] | boolean | NumberUnitAnswerValue | undefined;
export type QuestionRendererVariant = 'default' | 'popup';

/** Convention: other text for "Other" option is stored at `${questionKey}_otherText` in answers. */
export const OTHER_TEXT_SUFFIX = '_otherText';

export interface QuestionRendererProps {
  question: JsonQuestion;
  value: JsonAnswerValue;
  onChange: (questionKey: string, value: JsonAnswerValue) => void;
  /** Free-text companion value (from answers[questionKey + _otherText]) when the selected option requests details. */
  otherTextValue?: string | string[];
  error?: string;
  disabled?: boolean;
  variant?: QuestionRendererVariant;
  /** Hide the info tooltip for question.note (fill-manually / client link flows). */
  hideQuestionNote?: boolean;
  /** Used to evaluate skipRequiredWhen; when provided, required asterisk respects it */
  getAnswer?: (dependencyQuestionId: string) => JsonAnswerValue;
  /** Client age/sex fallback when optionVisibility age or sex answers are not yet in the form. */
  demographicContext?: QuestionnaireDemographicContext;
  /** Optional hook for flows that need to react to a specific option being selected. */
  onOptionSelect?: (question: JsonQuestion, option: JsonQuestionOption) => void;
}

function getPopupQuestionCode(question: JsonQuestion): string {
  if (question.isConditional || /__.+_followup$/i.test(question.externalId ?? question._id ?? '')) {
    return '';
  }

  const displayCode = question.displayCode?.trim().toUpperCase() ?? '';
  if (displayCode) return displayCode;

  const externalId = question.externalId?.trim().toUpperCase() ?? '';
  if (!externalId) return '';

  const suffixMatch = externalId.match(/(?:^|[-_\s])(\d{1,3}[A-Z]?)$/);
  const suffix = suffixMatch?.[1] ?? externalId;
  const labelPrefix = (question.label ?? '')
    .replace(/[^a-zA-Z]+/g, '')
    .slice(0, 3)
    .toUpperCase();

  if (!labelPrefix) return externalId;
  return `${labelPrefix}-${suffix}`;
}

function isTimeOrSkipSelect(question: JsonQuestion): boolean {
  if (question.type !== 'select') return false;
  const opt = question.options?.[0]?.label ?? '';
  // Backend currently encodes this as a single placeholder option.
  return /time\s*\(hh:mm\)\s*or\s*["“]?skipped\s+\w+["”]?/i.test(opt);
}

/** Option that triggers a date picker (e.g. "Other (specify date)"). */
function getOtherDateOption(question: JsonQuestion): { value: string; label: string } | null {
  if (question.type !== 'select' || !question.options?.length) return null;
  const found = question.options.find(
    (opt) =>
      /other/i.test(opt.label) &&
      (/specify/i.test(opt.label) || /date/i.test(opt.label))
  );
  return found ? { value: found.value, label: found.label } : null;
}

type OtherWithTextOption = { value: string; label: string };

function isGenderIdentityQuestion(question: JsonQuestion): boolean {
  const target = question.targetMapping;
  if (target?.collection === 'clients' && target.field === 'gender') return true;
  if (question._id === '696f888e02616daa6cbaea48' || question.externalId === '696f888e02616daa6cbaea48') return true;
  return question.questionText.trim().replace(/\s+/g, ' ').toLowerCase() === 'gender identity';
}

/**
 * Options that should reveal a companion free-text field.
 * Preferred signal: option.triggersOtherText === true.
 * Legacy fallback: allowOtherWithText + option value/label equals "other".
 */
function getOtherWithTextOptions(question: JsonQuestion): OtherWithTextOption[] {
  if (isGenderIdentityQuestion(question)) return [];
  if (!question.options?.length) return [];

  const explicit = question.options
    .filter((opt) => opt.triggersOtherText === true)
    .map((opt) => ({ value: opt.value, label: opt.label }));
  if (explicit.length) return explicit;

  if (!question.allowOtherWithText) return [];
  const legacy = question.options.find(
    (opt) =>
      String(opt.value).trim().toLowerCase() === 'other' ||
      String(opt.label).trim().toLowerCase() === 'other'
  );
  return legacy ? [{ value: legacy.value, label: legacy.label }] : [];
}

export function hasOtherWithTextOption(question: JsonQuestion): boolean {
  return getOtherWithTextOptions(question).length > 0;
}

function isOtherOptionSelected(
  value: JsonAnswerValue,
  otherOpts: OtherWithTextOption[],
  multi: boolean
): boolean {
  if (!otherOpts.length) return false;
  const triggerValues = new Set(otherOpts.map((opt) => opt.value));
  if (multi && Array.isArray(value)) return value.some((v) => triggerValues.has(v));
  if (!multi && typeof value === 'string') return triggerValues.has(value);
  return false;
}

function normalizeNoneExclusiveSelection(
  nextSelected: string[],
  previousSelected: string[],
  noneOptionIds: Set<string>
): string[] {
  if (noneOptionIds.size === 0) return nextSelected;

  const nextNoneId = nextSelected.find((id) => noneOptionIds.has(id));
  if (!nextNoneId) return nextSelected;

  const nextNonNone = nextSelected.filter((id) => !noneOptionIds.has(id));
  const previousHadNone = previousSelected.some((id) => noneOptionIds.has(id));
  const previousNonNone = previousSelected.filter((id) => !noneOptionIds.has(id));
  const addedNonNone = nextNonNone.some((id) => !previousNonNone.includes(id));

  if (previousHadNone && addedNonNone) {
    return nextNonNone;
  }

  return [nextNoneId];
}

/** YYYY-MM-DD format. */
function isDateString(v: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(v);
}

function extractSkippedText(question: JsonQuestion): string {
  const opt = question.options?.[0]?.label ?? '';
  // Extract "Skipped [meal]" from pattern like "Time (HH:MM) OR \"Skipped breakfast\""
  const match = opt.match(/[""]?(skipped\s+\w+)[""]?/i);
  if (match && match[1]) {
    // Capitalize first letter of each word
    return match[1]
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  return 'Skipped meal'; // fallback
}

/** True when question is type "text" with inputType "time" (e.g. breakfast time with optional "Skipped") OR type "time". */
function isTextTimeInput(question: JsonQuestion): boolean {
  return (question.type === 'text' && question.inputType === 'time') || question.type === 'time';
}

/** Extract "Skipped breakfast" (or similar) from pattern like "...|^Skipped breakfast$". */
function extractSkippedTextFromPattern(pattern: string | undefined): string {
  if (!pattern || typeof pattern !== 'string') return 'Skipped';
  const match = pattern.match(/\|\^?(Skipped [^$|]+)\$?/i);
  return match ? match[1].trim() : 'Skipped';
}

function isHHMM(value: string): boolean {
  // Basic 24h check: 00:00 - 23:59
  if (!/^\d{2}:\d{2}$/.test(value)) return false;
  const [hh, mm] = value.split(':').map((n) => Number(n));
  return Number.isFinite(hh) && Number.isFinite(mm) && hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59;
}

function isSkippedValue(value: string, skippedText: string): boolean {
  return typeof value === 'string' && value.trim().toLowerCase() === skippedText.toLowerCase();
}

function computeAgeFromDOB(dobValue: string): number | null {
  if (!isDateString(dobValue)) return null;
  const today = new Date();
  const [yyyy, mm, dd] = dobValue.split('-').map(Number);
  if (!yyyy || !mm || !dd) return null;
  let age = today.getFullYear() - yyyy;
  const m = today.getMonth() + 1 - mm;
  if (m < 0 || (m === 0 && today.getDate() < dd)) {
    age--;
  }
  return age;
}

/**
 * Derive whole years for optionVisibility bands.
 * - `years_integer_floor`: YYYY-MM-DD → age in years; or numeric string → Math.floor.
 * - Other ageUnit values: same resolution for backward compatibility.
 */
function resolveAgeYearsFloor(raw: JsonAnswerValue, ageUnit?: string): number | null {
  if (raw == null) return null;
  const str = typeof raw === 'string' ? raw.trim() : '';
  if (!str) return null;

  const unit = ageUnit ?? 'years_integer_floor';

  const fromDobOrNumber = (): number | null => {
    if (isDateString(str)) return computeAgeFromDOB(str);
    const n = Number.parseFloat(str.replace(/,/g, ''));
    if (Number.isFinite(n) && n >= 0) return Math.floor(n);
    return null;
  };

  if (unit === 'years_integer_floor' || unit === 'years') {
    return fromDobOrNumber();
  }
  return fromDobOrNumber();
}

function resolveSexForOptionVisibility(
  visibility: NonNullable<JsonQuestion['optionVisibility']>,
  getAnswer: (id: string) => JsonAnswerValue,
  demographicContext?: QuestionnaireDemographicContext,
): 'male' | 'female' | null {
  if (!visibility.sexForAthlete) {
    return demographicContext?.gender ?? null;
  }

  const { preferQuestionId, fallbackQuestionId, maleValues, femaleValues } = visibility.sexForAthlete;
  const preferVal = getAnswer(preferQuestionId);
  const fallbackVal = getAnswer(fallbackQuestionId);

  let sex: 'male' | 'female' | null = null;
  const pVal = typeof preferVal === 'string' ? preferVal : '';
  if (maleValues.includes(pVal)) sex = 'male';
  else if (femaleValues.includes(pVal)) sex = 'female';

  if (!sex) {
    const fVal = typeof fallbackVal === 'string' ? fallbackVal : '';
    if (maleValues.includes(fVal)) sex = 'male';
    else if (femaleValues.includes(fVal)) sex = 'female';
  }

  return sex ?? demographicContext?.gender ?? null;
}

function resolveAgeForOptionVisibility(
  visibility: NonNullable<JsonQuestion['optionVisibility']>,
  getAnswer: (id: string) => JsonAnswerValue,
  demographicContext?: QuestionnaireDemographicContext,
): number | null {
  const ageAnswer = getAnswer(visibility.ageQuestionId);
  const fromAnswer = resolveAgeYearsFloor(ageAnswer, visibility.ageUnit);
  if (fromAnswer !== null) return fromAnswer;

  if (demographicContext?.age != null && Number.isFinite(demographicContext.age)) {
    return Math.max(0, Math.floor(demographicContext.age));
  }

  return null;
}

function buildOptionFromValue(
  value: string,
  optionByValue: Map<string, JsonQuestionOption>,
  bandLabels?: Record<string, string>,
): JsonQuestionOption | null {
  const existing = optionByValue.get(value);
  const label = bandLabels?.[value] ?? existing?.label;
  if (!label) return null;

  return {
    label,
    value,
    ...(existing?.triggersOtherText ? { triggersOtherText: existing.triggersOtherText } : {}),
  };
}

function buildSelectedValueOption(value: string): JsonQuestionOption | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return {
    value: trimmed,
    label: formatDisplayLabel(trimmed) || trimmed,
  };
}

function includeSelectedOptions(
  baseOptions: JsonQuestionOption[],
  selectedValues: string[],
): JsonQuestionOption[] {
  const seen = new Set<string>();
  const merged: JsonQuestionOption[] = [];

  baseOptions.forEach((option) => {
    if (!option.value || seen.has(option.value)) return;
    seen.add(option.value);
    merged.push(option);
  });

  selectedValues.forEach((selectedValue) => {
    const option = buildSelectedValueOption(selectedValue);
    if (!option || seen.has(option.value)) return;
    seen.add(option.value);
    merged.push(option);
  });

  return merged;
}

function parseOtherTextChips(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => item.trim()).filter(Boolean);
  }
  if (!value?.trim()) return [];
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function serializeOtherTextChips(chips: string[]): string[] {
  return chips.map((chip) => chip.trim()).filter(Boolean);
}

function OtherTextInput({
  questionKey,
  label,
  value,
  onChange,
  disabled,
  asChips = false,
}: {
  questionKey: string;
  label: string;
  value?: string | string[];
  onChange: (questionKey: string, value: JsonAnswerValue) => void;
  disabled: boolean;
  asChips?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const chips = useMemo(() => parseOtherTextChips(value), [value]);

  const commitDraft = () => {
    const nextItems = parseOtherTextChips(draft);
    if (nextItems.length === 0) return;
    onChange(questionKey, serializeOtherTextChips([...chips, ...nextItems]));
    setDraft('');
  };

  const removeChip = (chipIndex: number) => {
    onChange(questionKey, serializeOtherTextChips(chips.filter((_, index) => index !== chipIndex)));
  };

  if (!asChips) {
    return (
      <div className="pl-1 space-y-2">
        <Label className="text-sm text-muted-foreground">{label}</Label>
        <Input
          type="text"
          value={Array.isArray(value) ? value.join(', ') : typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(questionKey, e.target.value)}
          placeholder="Enter details..."
          disabled={disabled}
          className="rounded-lg max-w-md"
        />
      </div>
    );
  }

  return (
    <div className="pl-1 space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <Label className="text-sm text-muted-foreground">{label}</Label>
        <span className="text-xs text-muted-foreground">Separate them by commas</span>
      </div>
      <div
        className={cn(
          'min-h-[52px] w-full rounded-lg border border-input bg-white px-3 py-2 shadow-sm',
          'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          disabled && 'bg-muted/30'
        )}
      >
        <TagList gap="sm" className="min-h-8">
          {chips.map((chip, index) => {
            const displayLabel = formatDisplayLabel(chip) || chip.replace(/[_-]+/g, ' ');
            return (
              <Tag
                key={`${chip}-${index}`}
                variant="selected"
                size="md"
                maxWidth="100%"
                truncate={false}
                className="shrink-0 border-[#20AEDC] bg-[#20AEDC] text-white"
                onRemove={disabled ? undefined : () => removeChip(index)}
                removeAriaLabel={`Remove ${displayLabel}`}
              >
                {displayLabel}
              </Tag>
            );
          })}
          {!disabled ? (
            <input
              type="text"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={commitDraft}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ',') {
                  event.preventDefault();
                  commitDraft();
                }
              }}
              placeholder={chips.length === 0 ? 'Enter details...' : ''}
              className="h-8 min-w-[180px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          ) : null}
        </TagList>
      </div>
    </div>
  );
}

export function isNumberUnitAnswerValue(value: unknown): value is NumberUnitAnswerValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    'unit' in value &&
    typeof (value as { unit?: unknown }).unit === 'string'
  );
}

export function isAnsweredValue(value: JsonAnswerValue): boolean {
  if (isNumberUnitAnswerValue(value)) {
    const unit = value.unit.trim();
    const numericValue = Number(value.value);
    return unit.length > 0 && Number.isFinite(numericValue);
  }

  return (
    (typeof value === 'string' && value.trim().length > 0) ||
    (Array.isArray(value) && value.length > 0) ||
    (typeof value === 'boolean' && value === true)
  );
}

function getVisibleOptions(
  question: JsonQuestion,
  getAnswer?: (id: string) => JsonAnswerValue,
  demographicContext?: QuestionnaireDemographicContext,
): import('./types').JsonQuestionOption[] {
  if (!question.options) return [];
  const visibility = question.optionVisibility;
  if (!visibility) return question.options;

  const answerLookup = getAnswer ?? (() => undefined);
  const age = resolveAgeForOptionVisibility(visibility, answerLookup, demographicContext);
  if (age === null) return [];

  const band = visibility.bands.find((b) => age >= b.ageMin && age <= b.ageMax);
  if (!band) return [];

  const sex = resolveSexForOptionVisibility(visibility, answerLookup, demographicContext);
  const orderedValues = [...band.visibleOptionValues];

  if (band.athleteOptionValuesBySex && sex === 'male') {
    orderedValues.push(...band.athleteOptionValuesBySex.male);
  } else if (band.athleteOptionValuesBySex && sex === 'female') {
    orderedValues.push(...band.athleteOptionValuesBySex.female);
  }

  const optionByValue = new Map((question.options ?? []).map((option) => [option.value, option]));

  return orderedValues
    .map((value) => buildOptionFromValue(value, optionByValue, band.labels))
    .filter((option): option is JsonQuestionOption => Boolean(option));
}

/** Parse single number for split unit (e.g. totalInches → [feet, inches]). */
function parseSplitUnitValue(
  numberPart: string,
  submitFormat: 'totalInches' | 'feetDecimal'
): number[] {
  const num = parseFloat(numberPart) || 0;
  if (submitFormat === 'totalInches') {
    const feet = Math.floor(num / 12);
    const inches = roundMeasurementValue(num - feet * 12, 2);
    return [feet, inches];
  }
  const feet = Math.floor(num);
  const inches = roundMeasurementValue((num - feet) * 12, 2);
  return [feet, inches];
}

/** Encode split values (e.g. [feet, inches]) into single number for submit. */
function encodeSplitUnitValue(
  parts: number[],
  submitFormat: 'totalInches' | 'feetDecimal'
): number {
  const [feet = 0, inches = 0] = parts;
  if (submitFormat === 'totalInches') return feet * 12 + inches;
  return feet + inches / 12;
}

function parseFeetInchesText(raw: string): number | null {
  const match = raw
    .trim()
    .match(/^(\d+)\s*(?:'|ft|feet)\s*(?:(\d+(?:\.\d+)?)\s*(?:"|in|inches)?)?\s*(?:ft\/in)?$/i);
  if (!match) return null;

  const feet = Number.parseFloat(match[1]);
  const inches = match[2] ? Number.parseFloat(match[2]) : 0;
  if (!Number.isFinite(feet) || !Number.isFinite(inches)) return null;
  return feet * 12 + inches;
}

function makeNumberUnitAnswer(value: string | number, unit: string): NumberUnitAnswerValue {
  return { value, unit };
}

const CM_PER_INCH = 2.54;
const LBS_PER_KG = 2.20462262185;

function normalizeMeasurementUnit(unit: string): string {
  return unit.trim().toLowerCase().replace(/\s+/g, '');
}

function roundMeasurementValue(value: number, decimals = 1): number {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

function convertNumberUnitValue(value: number, fromUnit: string, toUnit: string): number | null {
  const from = normalizeMeasurementUnit(fromUnit);
  const to = normalizeMeasurementUnit(toUnit);

  if (from === to) return value;
  if (from === 'cm' && to === 'ft/in') return roundMeasurementValue(value / CM_PER_INCH, 1);
  if (from === 'ft/in' && to === 'cm') return roundMeasurementValue(value * CM_PER_INCH, 1);
  if (from === 'kg' && (to === 'lbs' || to === 'lb')) return roundMeasurementValue(value * LBS_PER_KG, 1);
  if ((from === 'lbs' || from === 'lb') && to === 'kg') return roundMeasurementValue(value / LBS_PER_KG, 1);

  return null;
}

/** Resolve unit hint and canonical key. Tries exact then normalized (ft/in, ft_in, ft-in). Returns the key used in unitInputHints so submit uses backend's key. */
function getUnitHint(
  unitInputHints: Record<string, UnitInputHint> | undefined,
  unit: string
): { hint: UnitInputHint; canonicalKey: string } | undefined {
  if (!unitInputHints || !unit) return undefined;
  const candidates: [string, UnitInputHint][] = [
    [unit, unitInputHints[unit]],
    [unit.replace(/\//g, '_'), unitInputHints[unit.replace(/\//g, '_')]],
    [unit.replace(/_/g, '/'), unitInputHints[unit.replace(/_/g, '/')]],
    [unit.replace(/\//g, '_').replace(/-/g, '_'), unitInputHints[unit.replace(/\//g, '_').replace(/-/g, '_')]],
  ];
  for (const [key, hint] of candidates) {
    if (hint) return { hint, canonicalKey: key };
  }
  const withHyphen = unit.replace(/_/g, '-');
  if (unitInputHints[withHyphen]) return { hint: unitInputHints[withHyphen], canonicalKey: withHyphen };
  return undefined;
}

function NumberUnitInput({
  questionKey,
  value,
  onChange,
  allowedUnits,
  unitInputHints,
  placeholder,
  disabled,
  min,
  max,
}: {
  questionKey: string;
  value: JsonAnswerValue;
  onChange: (questionKey: string, value: JsonAnswerValue) => void;
  allowedUnits: string[] | undefined;
  unitInputHints?: Record<string, UnitInputHint>;
  placeholder: string;
  disabled: boolean;
  min?: number;
  max?: number;
}) {
  const unitOptions =
    allowedUnits?.length ? allowedUnits.map((u) => ({ label: u, value: u })) : [];
  const firstUnit = unitOptions[0]?.value ?? '';
  const { numberPart, unitPart } = parseNumberUnitValue(value);
  const currentUnit =
    unitPart && unitOptions.some((o) => o.value === unitPart) ? unitPart : firstUnit;
  const hintResult = getUnitHint(unitInputHints, currentUnit);
  const hint = hintResult?.hint;
  const canonicalUnitKey = hintResult?.canonicalKey ?? currentUnit;
  const useSplitInput =
    hint?.splitInput === true &&
    (hint.fields?.length ?? 0) >= 2 &&
    (hint.submitFormat === 'totalInches' || hint.submitFormat === 'feetDecimal');

  const handleNumberChange = (num: string) => {
    if (unitOptions.length) {
      const unit =
        unitPart && unitOptions.some((o) => o.value === unitPart) ? unitPart : firstUnit;
      onChange(questionKey, num.trim() ? makeNumberUnitAnswer(num, unit) : unit);
    } else {
      onChange(questionKey, num.trim() || '');
    }
  };

  const handleSplitChange = (index: number, raw: string) => {
    const n = parseFloat(raw) || 0;
    const parsed = parseSplitUnitValue(numberPart || '0', hint!.submitFormat!);
    const next = [...parsed];
    next[index] = n;
    const combined = encodeSplitUnitValue(next, hint!.submitFormat!);
    onChange(questionKey, makeNumberUnitAnswer(combined, canonicalUnitKey));
  };

  if (useSplitInput && hint?.fields) {
    const parsed = parseSplitUnitValue(numberPart || '0', hint.submitFormat!);
    return (
      <div className="flex flex-wrap gap-2 items-stretch">
        {hint.fields.map((field, index) => (
          <div key={field.name} className="flex flex-col gap-1">
            {field.label ? (
              <Label className="text-xs text-muted-foreground">{field.label}</Label>
            ) : null}
            <Input
              type="number"
              value={parsed[index] ?? ''}
              onChange={(e) => handleSplitChange(index, e.target.value)}
              placeholder={field.label ?? field.name}
              disabled={disabled}
              min={field.min}
              max={field.max}
              step={index === 1 && hint.submitFormat === 'feetDecimal' ? '0.1' : 'any'}
              className="w-[72px] sm:w-[80px] rounded-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
        ))}
        {unitOptions.length > 0 ? (
          <div className="flex flex-col gap-1">
            {unitOptions.length > 1 ? (
              <>
                <Label className="text-xs text-muted-foreground">Unit</Label>
                <Select
                  value={currentUnit}
                  onValueChange={(unit) => {
                    const nextHintResult = getUnitHint(unitInputHints, unit);
                    const combined = encodeSplitUnitValue(
                      parsed,
                      hint.submitFormat!
                    );
                    const converted = convertNumberUnitValue(combined, currentUnit, unit);

                    if (nextHintResult?.hint?.splitInput !== true) {
                      if (!numberPart.trim()) {
                        onChange(questionKey, unit);
                        return;
                      }
                      onChange(
                        questionKey,
                        makeNumberUnitAnswer(converted ?? combined, unit)
                      );
                      return;
                    }
                    const nextCanonical = nextHintResult?.canonicalKey ?? unit;
                    onChange(questionKey, makeNumberUnitAnswer(converted ?? combined, nextCanonical));
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger className="w-[88px] sm:w-[100px] shrink-0 rounded-lg h-10">
                    <SelectValue placeholder="Unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {unitOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            ) : (
              <div className="h-10 flex items-center text-sm text-muted-foreground pl-2">
                {currentUnit}
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex gap-2 items-stretch">
      <Input
        type="number"
        value={numberPart}
        onChange={(e) => handleNumberChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        max={max}
        step="any"
        className="flex-1 min-w-0 rounded-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      {unitOptions.length > 0 ? (
        <Select
          value={currentUnit}
          onValueChange={(unit) => {
            const nextHintResult = getUnitHint(unitInputHints, unit);
            const nextUnit = nextHintResult?.canonicalKey ?? unit;
            const numericValue = Number(numberPart);

            if (nextHintResult?.hint?.splitInput === true) {
              if (!numberPart.trim() || !Number.isFinite(numericValue)) {
                onChange(questionKey, nextUnit);
                return;
              }
              const converted = convertNumberUnitValue(numericValue, currentUnit, nextUnit);
              onChange(questionKey, makeNumberUnitAnswer(converted ?? numericValue, nextUnit));
              return;
            }
            if (!numberPart.trim() || !Number.isFinite(numericValue)) {
              onChange(questionKey, nextUnit);
              return;
            }
            const converted = convertNumberUnitValue(numericValue, currentUnit, nextUnit);
            onChange(questionKey, makeNumberUnitAnswer(converted ?? numberPart, nextUnit));
          }}
          disabled={disabled}
        >
          <SelectTrigger className="w-[88px] sm:w-[100px] shrink-0 rounded-lg">
            <SelectValue placeholder="Unit" />
          </SelectTrigger>
          <SelectContent>
            {unitOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
    </div>
  );
}

/** Parse stored value "3 kg" into { numberPart, unitPart }. */
function parseNumberUnitValue(value: JsonAnswerValue): { numberPart: string; unitPart: string } {
  if (isNumberUnitAnswerValue(value)) {
    return { numberPart: String(value.value ?? ''), unitPart: value.unit.trim() };
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return { numberPart: '', unitPart: '' };
  }
  const trimmed = value.trim();
  const match = trimmed.match(/^([\d.]*)\s*(.*)$/);
  if (match) {
    return { numberPart: match[1] ?? '', unitPart: (match[2] ?? '').trim() };
  }
  return { numberPart: trimmed, unitPart: '' };
}

function unitForSubmit(question: JsonQuestion, unit: string): string {
  const trimmed = unit.trim();
  const hintedUnit = getUnitHint(question.unitInputHints, trimmed)?.canonicalKey;
  if (hintedUnit) return hintedUnit;

  const allowedUnits = question.validation?.allowedUnits ?? [];
  const matchingAllowedUnit = allowedUnits.find((candidate) => candidate.trim() === trimmed);
  return matchingAllowedUnit ?? trimmed;
}

function normalizeNumberUnitAnswerForSubmit(question: JsonQuestion, value: unknown): unknown {
  if (isNumberUnitAnswerValue(value)) {
    const numericValue = Number(value.value);
    const unit = unitForSubmit(question, value.unit);
    if (!Number.isFinite(numericValue) || !unit) return undefined;
    return { value: numericValue, unit };
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;

    const footInchesTotal = parseFeetInchesText(trimmed);
    if (footInchesTotal != null) {
      return { value: footInchesTotal, unit: 'ft/in' };
    }

    const { numberPart, unitPart } = parseNumberUnitValue(trimmed);
    const numericValue = Number(numberPart);
    const fallbackUnit = question.validation?.allowedUnits?.[0] ?? '';
    const unit = unitForSubmit(question, unitPart || fallbackUnit);
    if (!Number.isFinite(numericValue) || !unit) return undefined;
    return { value: numericValue, unit };
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const unit = question.targetMapping?.canonicalUnit ?? question.validation?.allowedUnits?.[0] ?? '';
    return unit ? { value, unit } : value;
  }

  return value;
}

export function normalizeQuestionAnswerForSubmit(question: JsonQuestion, value: JsonAnswerValue): unknown {
  if (question.type === 'number-unit') {
    return normalizeNumberUnitAnswerForSubmit(question, value);
  }

  return value;
}

function getInferredScaleLabels(question: JsonQuestion): { min: string; max: string } {
  const explicitMin = question.scaleLabels?.min?.trim();
  const explicitMax = question.scaleLabels?.max?.trim();
  if (explicitMin && explicitMax) {
    return { min: explicitMin, max: explicitMax };
  }

  const text = question.questionText.toLowerCase();
  let inferred = { min: 'Low', max: 'High' };

  if (text.includes('motivated') || text.includes('motivation')) {
    inferred = { min: 'Not motivated', max: 'Very motivated' };
  } else if (text.includes('confidence') || text.includes('confident')) {
    inferred = { min: 'Not confident', max: 'Very confident' };
  } else if (text.includes('eating habit') || text.includes('diet quality') || text.includes('sleep quality')) {
    inferred = { min: 'Poor', max: 'Excellent' };
  } else if (text.includes('satisfied') || text.includes('satisfaction')) {
    inferred = { min: 'Not satisfied', max: 'Very satisfied' };
  } else if (text.includes('stress')) {
    inferred = { min: 'No stress', max: 'High stress' };
  } else if (text.includes('pain')) {
    inferred = { min: 'No pain', max: 'Severe pain' };
  } else if (text.includes('energy')) {
    inferred = { min: 'Very low', max: 'Very high' };
  } else if (text.includes('rate')) {
    inferred = { min: 'Poor', max: 'Excellent' };
  }

  return {
    min: explicitMin || inferred.min,
    max: explicitMax || inferred.max,
  };
}

function ScalePicker({
  questionKey,
  value,
  onChange,
  min,
  max,
  labels,
  disabled,
}: {
  questionKey: string;
  value: JsonAnswerValue;
  onChange: (questionKey: string, value: JsonAnswerValue) => void;
  min: number;
  max: number;
  labels: { min: string; max: string };
  disabled: boolean;
}) {
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  const current = typeof value === 'string' && value.trim() !== '' ? parseInt(value, 10) : null;
  const num = current !== null && !Number.isNaN(current) && current >= lo && current <= hi ? current : null;
  const options = Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {options.map((n) => (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onChange(questionKey, String(n))}
            className={cn(
              'h-11 min-w-[3rem] rounded-lg border-2 px-4 text-sm font-semibold transition-all',
              num === n
                ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                : 'border-input bg-muted/30 hover:border-muted-foreground/40 hover:bg-muted/50',
              disabled && 'pointer-events-none opacity-70'
            )}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="flex max-w-[640px] items-center justify-between gap-4 text-xs font-medium text-muted-foreground">
        <span>{lo} = {labels.min}</span>
        <span className="text-right">{hi} = {labels.max}</span>
      </div>
    </div>
  );
}

export function QuestionRenderer({
  question,
  value,
  onChange,
  otherTextValue,
  error,
  disabled = false,
  variant = 'default',
  hideQuestionNote = false,
  getAnswer,
  demographicContext,
  onOptionSelect,
}: QuestionRendererProps) {
  const questionKey = getQuestionKey(question);
  const isPopupVariant = variant === 'popup';
  const otherWithTextOpts = getOtherWithTextOptions(question);
  const shouldShowOtherTextForSingle = isOtherOptionSelected(value, otherWithTextOpts, false);
  const shouldShowOtherTextForMulti = isOtherOptionSelected(value, otherWithTextOpts, true);
  const required =
    getAnswer != null
      ? isEffectivelyRequired(question, getAnswer)
      : question.validation?.required === true;
  const questionNote = typeof question.note === 'string' ? question.note.trim() : '';
  const visibility = question.optionVisibility;
  const ageSourceAnswer = visibility && getAnswer ? getAnswer(visibility.ageQuestionId) : undefined;
  const preferVal = visibility?.sexForAthlete && getAnswer ? getAnswer(visibility.sexForAthlete.preferQuestionId) : undefined;
  const fallbackVal = visibility?.sexForAthlete && getAnswer ? getAnswer(visibility.sexForAthlete.fallbackQuestionId) : undefined;
  const demographicAge = demographicContext?.age;
  const demographicGender = demographicContext?.gender;

  const options = useMemo(() => {
    return getVisibleOptions(question, getAnswer, demographicContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, ageSourceAnswer, preferVal, fallbackVal, demographicAge, demographicGender, getAnswer, demographicContext]);
  const useFullWidthOptionButtons =
    isPopupVariant &&
    question.type === 'select' &&
    options.some((opt) => opt.label.length > 80);
  const questionCode = getPopupQuestionCode(question);
  const noneOptionIds = useMemo(
    () => new Set(options.filter(isNoneOption).map((option) => option.value)),
    [options]
  );
  const selectedMultiValues = useMemo(
    () => (Array.isArray(value) ? value.map((item) => String(item ?? '').trim()).filter(Boolean) : []),
    [value],
  );
  const isNoneSelected = selectedMultiValues.some((id) => noneOptionIds.has(id));
  const multiSelectOptions = useMemo(
    () => (question.type === 'multi-select' && isNoneSelected
      ? options.filter((option) => selectedMultiValues.includes(option.value) || noneOptionIds.has(option.value))
      : options),
    [isNoneSelected, noneOptionIds, options, question.type, selectedMultiValues]
  );
  const selectedAwareMultiSelectOptions = useMemo(
    () => includeSelectedOptions(multiSelectOptions, selectedMultiValues),
    [multiSelectOptions, selectedMultiValues],
  );

  useEffect(() => {
    if (!question.optionVisibility || disabled) return;

    if (typeof value === 'string' && value.trim()) {
      const isOther = isOtherOptionSelected(value, otherWithTextOpts, false);
      // Wait, isDateString is also a valid "other" value for dates
      const isDate = question.type === 'select' && isDateString(value);
      if (!isOther && !isDate && !options.some((opt) => opt.value === value)) {
        onChange(questionKey, '');
      }
    } else if (question.type === 'checkbox' && options.length > 1 && typeof value === 'boolean') {
      onChange(questionKey, '');
    } else if (Array.isArray(value) && value.length > 0) {
      const validOptions = value.filter((v) => {
        if (isOtherOptionSelected([v], otherWithTextOpts, true)) return true;
        return options.some((opt) => opt.value === v);
      });
      if (validOptions.length !== value.length) {
        // Multi-select currently only supports string array in our types
        onChange(questionKey, validOptions.length > 0 ? validOptions : []);
      }
    }
  }, [options, value, questionKey, onChange, question.optionVisibility, disabled, otherWithTextOpts, question.type]);

  useEffect(() => {
    if (question.type !== 'multi-select' || !Array.isArray(value) || value.length <= 1) return;

    const normalizedSelection = normalizeNoneExclusiveSelection(value, [], noneOptionIds);
    if (normalizedSelection.length !== value.length || normalizedSelection.some((id, index) => id !== value[index])) {
      onChange(questionKey, normalizedSelection);
    }
  }, [noneOptionIds, onChange, question.type, questionKey, value]);

  const isPlainNumberType =
    question.type === 'number' ||
    question.inputType === 'number';
  const isNumberUnitType = question.type === 'number-unit';

  const isTimeOrSkipSelectType = isTimeOrSkipSelect(question);
  const isTextTimeWithSkip =
    isTextTimeInput(question) && (question.validation?.pattern?.includes('Skipped') ?? false);
  const isTimeOrSkip = isTimeOrSkipSelectType || isTextTimeWithSkip;
  const skippedText = isTimeOrSkipSelectType
    ? extractSkippedText(question)
    : isTextTimeWithSkip
      ? extractSkippedTextFromPattern(question.validation?.pattern)
      : '';

  const timeOrSkipModeFromValue = useMemo<'time' | 'skipped' | 'none'>(() => {
    if (!isTimeOrSkip || typeof value !== 'string') {
      return isPopupVariant ? 'none' : 'time';
    }
    if (isSkippedValue(value, skippedText)) {
      return 'skipped';
    }
    if (isHHMM(value)) {
      return 'time';
    }
    return isPopupVariant ? 'none' : 'time';
  }, [value, isTimeOrSkip, skippedText, isPopupVariant]);
  const [timeOrSkipMode, setTimeOrSkipMode] = useState<'time' | 'skipped' | 'none'>(timeOrSkipModeFromValue);
  useEffect(() => {
    setTimeOrSkipMode(timeOrSkipModeFromValue);
  }, [timeOrSkipModeFromValue]);
  const currentTimeValue = typeof value === 'string' && isHHMM(value) ? value : '';
  const currentStringValue = typeof value === 'string' ? value : '';
  const isSkippedSelected = isTimeOrSkip && isSkippedValue(currentStringValue, skippedText);
  const isPopupTimeLocked = timeOrSkipMode !== 'time';

  const switchToTimeMode = () => {
    if (disabled) return;
    setTimeOrSkipMode('time');
    if (!isHHMM(currentStringValue)) {
      onChange(questionKey, '');
    }
  };

  const toggleSkippedMode = () => {
    if (disabled) return;
    if (isSkippedSelected) {
      setTimeOrSkipMode('none');
      onChange(questionKey, '');
      return;
    }
    setTimeOrSkipMode('skipped');
    onChange(questionKey, skippedText);
  };

  const selectOption = (option: JsonQuestionOption, nextValue: JsonAnswerValue) => {
    onOptionSelect?.(question, option);
    onChange(questionKey, nextValue);
  };

  return (
    <div
      data-question-card
      className={cn(
        isPopupVariant
          ? 'space-y-3 bg-transparent p-0 border-0 rounded-none shadow-none transition-colors'
          : 'rounded-xl border border-border/80 bg-card p-5 space-y-4 shadow-sm transition-colors',
        error && (isPopupVariant ? 'rounded-2xl bg-[#fff7f7] px-3 py-3 ring-1 ring-[#f5c2c7]' : 'border-destructive/60 bg-destructive/5'),
        !error && !isPopupVariant && 'hover:border-muted-foreground/20',
        disabled && 'opacity-60 grayscale'
      )}
    >
      <div className={cn('min-w-0', isPopupVariant && 'flex items-start gap-3')}>
        {isPopupVariant && questionCode ? (
          <p className="min-w-[54px] pt-0.5 text-[12px] font-medium tracking-[0.04em] text-[#A0AABA]">
            {questionCode}
          </p>
        ) : null}
        <div className="flex min-w-0 items-start gap-2">
          <label className="text-[15px] font-medium leading-snug text-foreground">
            {required ? <span className="text-destructive mr-1" aria-hidden>*</span> : null}
            {question.questionText}
          </label>
          {questionNote && !hideQuestionNote ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    aria-label="Question guidance"
                  >
                    <Info className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-sm text-xs leading-relaxed">
                  {questionNote}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
        </div>
      </div>

      {isTimeOrSkip ? (
        <div className="space-y-3">
          {isPopupVariant ? (
            <div className="flex flex-wrap items-center gap-3">
              <div
                className={cn(
                  'inline-flex min-h-[46px] items-center gap-3 rounded-full border px-3 py-1.5 transition-colors',
                  timeOrSkipMode === 'time'
                    ? 'border-[#19a7d9] bg-[#e9f6fc]'
                    : 'border-[#cfd8e3] bg-[#f8fafc]'
                )}
              >
                <button
                  type="button"
                  onClick={switchToTimeMode}
                  disabled={disabled}
                  className={cn('whitespace-nowrap text-[15px] font-semibold text-[#4b5565]', disabled && 'cursor-not-allowed opacity-70')}
                >
                  Time (HH:MM)
                </button>
                <Input
                  type="time"
                  value={currentTimeValue}
                  onChange={(e) => {
                    if (disabled) return;
                    setTimeOrSkipMode('time');
                    onChange(questionKey, e.target.value);
                  }}
                  onClick={() => {
                    if (disabled) return;
                    switchToTimeMode();
                  }}
                  onFocus={switchToTimeMode}
                  disabled={disabled}
                  readOnly={isPopupTimeLocked}
                  className="h-10 w-full min-w-[140px] sm:min-w-[180px] max-w-[220px] rounded-2xl border-0 bg-transparent shadow-none read-only:opacity-100 [&_.MuiOutlinedInput-notchedOutline]:!border-0 [&_.MuiOutlinedInput-input]:!bg-transparent [&_.MuiOutlinedInput-input]:!px-1 [&_.MuiOutlinedInput-input]:!py-0"
                />
              </div>
              <button
                type="button"
                onClick={toggleSkippedMode}
                disabled={disabled}
                className={cn(
                  'inline-flex min-h-[46px] items-center rounded-full border px-5 sm:px-7 py-1.5 text-[16px] sm:text-[17px] font-semibold transition-colors',
                  isSkippedSelected
                    ? 'border-[#19a7d9] bg-[#e9f6fc] text-[#0b88bc]'
                    : 'border-[#cfd8e3] bg-[#f8fafc] text-[#4b5565]',
                  disabled && 'cursor-not-allowed opacity-70'
                )}
              >
                {skippedText}
              </button>
            </div>
          ) : (
            <RadioGroup
              value={timeOrSkipMode}
              onValueChange={(next) => {
                if (disabled) return;
                const mode = next === 'skipped' ? 'skipped' : 'time';
                setTimeOrSkipMode(mode);
                if (mode === 'skipped') {
                  onChange(questionKey, skippedText);
                } else {
                  const existing = typeof value === 'string' ? value : '';
                  onChange(questionKey, isHHMM(existing) ? existing : '');
                }
              }}
              className="gap-3"
            >
              <div className="rounded-lg border-2 border-border bg-muted/20 px-4 py-3 space-y-3 transition-colors has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5">
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="time" id={`${questionKey}-mode-time`} disabled={disabled} />
                  <Label htmlFor={`${questionKey}-mode-time`} className={cn('text-sm font-medium cursor-pointer', disabled && 'opacity-70')}>
                    Time (HH:MM)
                  </Label>
                </div>
                <div className="pl-7">
                  <Input
                    type="time"
                    value={currentTimeValue}
                    onChange={(e) => {
                      if (disabled) return;
                      setTimeOrSkipMode('time');
                      onChange(questionKey, e.target.value);
                    }}
                    disabled={disabled || timeOrSkipMode !== 'time'}
                    className="w-full max-w-[220px] sm:w-[180px] rounded-lg"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-3 rounded-lg border-2 border-border bg-muted/20 px-4 py-3 transition-colors hover:bg-muted/40 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5">
                <RadioGroupItem value="skipped" id={`${questionKey}-mode-skipped`} disabled={disabled} />
                <Label htmlFor={`${questionKey}-mode-skipped`} className={cn('text-sm font-medium cursor-pointer', disabled && 'opacity-70')}>
                  {skippedText}
                </Label>
              </div>
            </RadioGroup>
          )}
        </div>
      ) : null}

      {question.type === 'select' && !isTimeOrSkip ? (
        <div className="space-y-3">
          {isPopupVariant ? (
            <div className={cn(useFullWidthOptionButtons ? 'grid gap-3' : 'flex flex-wrap gap-3')}>
              {options.map((opt, idx) => {
                const otherOpt = getOtherDateOption(question);
                const selectedValue =
                  typeof value === 'string' && isDateString(value)
                    ? (otherOpt?.value ?? value)
                    : (typeof value === 'string' ? value : '');
                const isSelected = selectedValue === opt.value;

                return (
                  <button
                    key={`${opt.value}-${idx}`}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      if (otherOpt && opt.value === otherOpt.value) {
                        const currentDate = typeof value === 'string' && isDateString(value) ? value : '';
                        selectOption(opt, currentDate || opt.value);
                        return;
                      }
                      selectOption(opt, opt.value);
                    }}
                    className={cn(
                      'inline-flex min-h-[38px] items-center rounded-full border text-[14px] font-semibold transition-colors',
                      useFullWidthOptionButtons
                        ? 'w-full justify-center whitespace-normal px-5 py-3 text-center leading-relaxed'
                        : 'px-4 py-1.5',
                      isSelected
                        ? 'border-[#24A8DA] bg-[#E9F7FD] text-[#1588BB]'
                        : 'border-[#D3D9E2] bg-[#F8FAFC] text-[#4B5565] hover:border-[#A8D8EE] hover:bg-[#F3FAFE]',
                      disabled && 'cursor-not-allowed opacity-60'
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <Select
              value={
                typeof value === 'string' && isDateString(value)
                  ? (getOtherDateOption(question)?.value ?? value)
                  : (typeof value === 'string' ? value : '')
              }
              onValueChange={(next) => {
                const otherOpt = getOtherDateOption(question);
                const selectedOption = options.find((opt) => opt.value === next);
                if (otherOpt && next === otherOpt.value) {
                  // Keep current date if already set, otherwise leave as "other" until date picked
                  const currentDate = typeof value === 'string' && isDateString(value) ? value : '';
                  if (selectedOption) selectOption(selectedOption, currentDate || next);
                  else onChange(questionKey, currentDate || next);
                } else {
                  if (selectedOption) selectOption(selectedOption, next);
                  else onChange(questionKey, next);
                }
              }}
              disabled={disabled}
            >
              <SelectTrigger className="w-full rounded-lg h-11">
                <SelectValue placeholder="Select an option..." />
              </SelectTrigger>
              <SelectContent>
                {options.map((opt, idx) => (
                  <SelectItem key={`${opt.value}-${idx}`} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {(() => {
            const otherOpt = getOtherDateOption(question);
            if (!otherOpt) return null;
            const selectVal = typeof value === 'string' ? value : '';
            const isOtherSelected =
              selectVal === otherOpt.value || isDateString(selectVal);
            if (!isOtherSelected) return null;
            return (
              <div className="pl-1">
                <label className="text-sm text-muted-foreground mb-2 block">
                  Select date
                </label>
                <Input
                  type="date"
                  value={isDateString(selectVal) ? selectVal : ''}
                  onChange={(e) => onChange(questionKey, e.target.value || otherOpt.value)}
                  disabled={disabled}
                  className="w-full max-w-[220px] rounded-lg"
                />
              </div>
            );
          })()}
          {shouldShowOtherTextForSingle ? (
            <OtherTextInput
              questionKey={questionKey + OTHER_TEXT_SUFFIX}
              label="Please specify"
              value={otherTextValue}
              onChange={onChange}
              disabled={disabled}
            />
          ) : null}
        </div>
      ) : null}

      {question.type === 'radio' ? (
        <div className="space-y-3">
          <RadioGroup
            value={typeof value === 'string' ? value : ''}
            onValueChange={(next) => {
              const selectedOption = options.find((opt) => opt.value === next);
              if (selectedOption) {
                selectOption(selectedOption, next);
                return;
              }
              onChange(questionKey, next);
            }}
            disabled={disabled}
            className="gap-3"
          >
            {options.map((opt, idx) => {
              const optionId = `${questionKey}-radio-${idx}`;
              return (
                <div
                  key={`${opt.value}-${idx}`}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  onClick={() => {
                    if (disabled) return;
                    selectOption(opt, opt.value);
                  }}
                  onKeyDown={(event) => {
                    if (disabled) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      selectOption(opt, opt.value);
                    }
                  }}
                  className={cn(
                    'flex items-center space-x-3 rounded-lg border-2 border-border bg-muted/20 px-4 py-3 transition-colors has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5',
                    !disabled && 'cursor-pointer hover:bg-muted/40',
                    disabled && 'cursor-not-allowed opacity-70'
                  )}
                >
                  <RadioGroupItem value={opt.value} id={optionId} disabled={disabled} />
                  <Label
                    htmlFor={optionId}
                    className={cn('text-sm font-medium cursor-pointer', disabled && 'opacity-70')}
                  >
                    {opt.label || opt.value}
                  </Label>
                </div>
              );
            })}
          </RadioGroup>
          {shouldShowOtherTextForSingle ? (
            <OtherTextInput
              questionKey={questionKey + OTHER_TEXT_SUFFIX}
              label="Please specify"
              value={otherTextValue}
              onChange={onChange}
              disabled={disabled}
            />
          ) : null}
        </div>
      ) : null}

      {question.type === 'multi-select' ? (
        <div className="space-y-3">
          <MultiSelect
            options={selectedAwareMultiSelectOptions.map((opt) => ({
              id: opt.value,
              name: opt.label || formatDisplayLabel(opt.value) || opt.value,
            }))}
            selected={selectedMultiValues}
            onChange={(selected) => {
              const normalizedSelection = normalizeNoneExclusiveSelection(selected, selectedMultiValues, noneOptionIds);
              const addedOptions = normalizedSelection
                .filter((id) => !selectedMultiValues.includes(id))
                .map((id) => selectedAwareMultiSelectOptions.find((option) => option.value === id))
                .filter((option): option is JsonQuestionOption => Boolean(option));

              addedOptions.forEach((option) => onOptionSelect?.(question, option));
              onChange(questionKey, normalizedSelection);
              if (!isOtherOptionSelected(normalizedSelection, otherWithTextOpts, true) && otherTextValue !== undefined) {
                onChange(questionKey + OTHER_TEXT_SUFFIX, undefined);
              }
            }}
            placeholder={
              question.validation?.maxSelections
                ? `Select up to ${question.validation.maxSelections} options...`
                : 'Select options...'
            }
            disabled={disabled}
            maxSelections={question.validation?.maxSelections}
            className="rounded-lg"
          />
          {shouldShowOtherTextForMulti ? (
            <OtherTextInput
              questionKey={questionKey + OTHER_TEXT_SUFFIX}
              label="Please specify (Other)"
              value={otherTextValue}
              onChange={onChange}
              disabled={disabled}
              asChips
            />
          ) : null}
        </div>
      ) : null}

      {question.type === 'textarea' ? (
        <Textarea
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(questionKey, e.target.value)}
          placeholder={question.options?.[0]?.label ?? ''}
          disabled={disabled}
          className="min-h-[120px] rounded-lg resize-y"
        />
      ) : null}

      {question.type === 'checkbox' && options.length > 1 ? (
        <div className="space-y-3">
          <RadioGroup
            value={typeof value === 'string' ? value : ''}
            onValueChange={(next) => {
              const selectedOption = options.find((opt) => opt.value === next);
              if (selectedOption) {
                selectOption(selectedOption, next);
                return;
              }
              onChange(questionKey, next);
            }}
            disabled={disabled}
            className="gap-3"
          >
            {options.map((opt, idx) => {
              const optionId = `${questionKey}-checkbox-choice-${idx}`;
              return (
                <div
                  key={`${opt.value}-${idx}`}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  onClick={() => {
                    if (disabled) return;
                    selectOption(opt, opt.value);
                  }}
                  onKeyDown={(event) => {
                    if (disabled) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      selectOption(opt, opt.value);
                    }
                  }}
                  className={cn(
                    'flex items-center space-x-3 rounded-lg border-2 border-border bg-muted/20 px-4 py-3 transition-colors has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5 min-w-0',
                    !disabled && 'cursor-pointer hover:bg-muted/40',
                    disabled && 'cursor-not-allowed opacity-70'
                  )}
                >
                  <RadioGroupItem value={opt.value} id={optionId} disabled={disabled} />
                  <Label
                    htmlFor={optionId}
                    className={cn(
                      'text-sm font-medium cursor-pointer flex-1 min-w-0 break-words whitespace-normal leading-normal',
                      disabled && 'opacity-70'
                    )}
                  >
                    {opt.label || opt.value}
                  </Label>
                </div>
              );
            })}
          </RadioGroup>
        </div>
      ) : null}

      {question.type === 'checkbox' && options.length <= 1 ? (
        <div
          className={cn(
            'flex items-center space-x-3 rounded-lg border-2 border-border bg-muted/20 px-4 py-3 transition-colors has-[[data-state=checked]]:border-primary/60 has-[[data-state=checked]]:bg-primary/5 min-w-0'
          )}
        >
          <Checkbox
            id={questionKey}
            checked={
              typeof value === 'boolean'
                ? value
                : typeof value === 'string' && value.trim().length > 0
                  ? value === (options[0]?.value ?? 'true') || value === 'true'
                  : false
            }
            disabled={disabled}
            className="shrink-0"
            onCheckedChange={(next) => {
              const optionValue = options[0]?.value;
              if (optionValue) {
                onChange(questionKey, next === true ? optionValue : '');
                return;
              }
              onChange(questionKey, next === true);
            }}
          />
          <Label
            htmlFor={questionKey}
            className={cn(
              'text-sm font-medium cursor-pointer flex-1 min-w-0 break-words whitespace-normal leading-normal',
              disabled && 'opacity-70'
            )}
          >
            {options[0]?.label ?? question.options?.[0]?.label ?? 'I agree'}
          </Label>
        </div>
      ) : null}

      {question.type === 'date' ? (
        <Input
          type="date"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(questionKey, e.target.value)}
          disabled={disabled}
          className="w-full max-w-[220px] rounded-lg"
        />
      ) : null}

      {question.type === 'scale' ? (
        <ScalePicker
          questionKey={questionKey}
          value={value}
          onChange={onChange}
          min={question.validation?.min ?? 1}
          max={question.validation?.max ?? 10}
          labels={getInferredScaleLabels(question)}
          disabled={disabled}
        />
      ) : null}

      {isNumberUnitType ? (
        <NumberUnitInput
          questionKey={questionKey}
          value={value}
          onChange={onChange}
          allowedUnits={question.validation?.allowedUnits}
          unitInputHints={question.unitInputHints}
          placeholder={question.placeholder ?? options?.[0]?.label ?? ''}
          disabled={disabled}
          min={question.validation?.min}
          max={question.validation?.max}
        />
      ) : null}

      {isPlainNumberType ? (
        <Input
          type="number"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(questionKey, e.target.value)}
          placeholder={question.placeholder ?? question.options?.[0]?.label ?? ''}
          disabled={disabled}
          min={question.validation?.min}
          max={question.validation?.max}
          step="any"
          className="w-full max-w-[220px] sm:max-w-[200px] rounded-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
      ) : null}

      {isTextTimeInput(question) && !isTextTimeWithSkip ? (
        <Input
          type="time"
          value={typeof value === 'string' && isHHMM(value) ? value : ''}
          onChange={(e) => onChange(questionKey, e.target.value)}
          disabled={disabled}
          className="w-full max-w-[220px] sm:w-[180px] rounded-lg"
        />
      ) : null}

      {question.type === 'text' && !isTextTimeInput(question) ? (
        <Input
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(questionKey, e.target.value)}
          placeholder={question.placeholder ?? question.options?.[0]?.label ?? ''}
          disabled={disabled}
          minLength={question.validation?.minLength}
          maxLength={question.validation?.maxLength}
          className="rounded-lg"
        />
      ) : null}

      {error ? <p className="text-sm text-destructive font-medium mt-1" role="alert">{error}</p> : null}
    </div>
  );
}
